import type { MetaFunction, LoaderFunctionArgs, ActionFunctionArgs } from 'react-router'
import { CONTENT_PAGE_HEADERS } from "~/lib/cache.server"
import { useLoaderData, Form, useActionData, useNavigation, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { questions, answers } from '@afficheservices/db/schema'
import { eq, desc, sql } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { Clock, Eye, BadgeCheck, ArrowLeft, ThumbsUp, ArrowRight } from 'lucide-react'
import { checkRateLimit, rateLimitResponse } from '~/lib/security.server'

export async function loader({ params }: LoaderFunctionArgs) {
  const { slug } = params
  const [question] = await db.select().from(questions).where(eq(questions.slug, slug ?? '')).limit(1)
  if (!question) throw new Response('Question non trouvée', { status: 404 })

  // Increment view count
  await db.update(questions).set({ viewCount: question.viewCount + 1 }).where(eq(questions.id, question.id))

  // Cap at 50 answers — popular questions can accumulate hundreds and the
  // unbounded loader was up to 10s on production. 50 covers >99% of UX
  // need; deeper history can come behind a "Voir plus" pagination later.
  const answerList = await db.select().from(answers)
    .where(eq(answers.questionId, question.id))
    .orderBy(desc(answers.upvotes), desc(answers.createdAt))
    .limit(50)

  return { question, answers: answerList }
}

export function headers() { return CONTENT_PAGE_HEADERS }

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: `${data?.question?.title ?? 'Question'} | AfficheServices` },
  { name: 'description', content: data?.question?.body?.slice(0, 160) },
  { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/questions/${data?.question?.slug}` },
]

export async function action({ request, params }: ActionFunctionArgs) {
  if (!checkRateLimit(request, 'form')) return rateLimitResponse()
  const formData = await request.formData()
  const body = (formData.get('body') as string)?.replace(/<[^>]*>/g, '').replace(/javascript:/gi, '').trim()
  const name = (formData.get('name') as string)?.replace(/<[^>]*>/g, '').trim()
  const isPro = formData.get('isPro') === 'true'

  if (!body || body.length < 10) return { error: 'La réponse doit contenir au moins 10 caractères' }
  if (!name) return { error: 'Votre nom est requis' }

  const [question] = await db.select({ id: questions.id }).from(questions)
    .where(eq(questions.slug, params.slug ?? '')).limit(1)
  if (!question) return { error: 'Question introuvable' }

  await db.insert(answers).values({
    questionId: question.id,
    guestName: name,
    isProfessional: isPro,
    body,
  })

  await db.update(questions)
    .set({ answerCount: sql`${questions.answerCount} + 1`, updatedAt: new Date() })
    .where(eq(questions.id, question.id))

  return { success: true }
}

export default function QuestionDetail() {
  const { question, answers: answerList } = useLoaderData<typeof loader>()
  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Questions', url: 'https://www.afficheservices.fr/questions' },
        { name: question.title, url: `https://www.afficheservices.fr/questions/${question.slug}` },
      ]} />

      <div className="mx-auto max-w-3xl px-4 py-12 lg:px-6">
        <Link to="/questions" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
          <ArrowLeft className="h-3.5 w-3.5" /> Toutes les questions
        </Link>

        {/* Question */}
        <div className="mt-6 rounded-2xl border border-border bg-white p-6 shadow-sm">
          <h1 className="text-xl font-bold text-slate-900">{question.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-400">
            <span>Par {question.guestName ?? 'Anonyme'}</span>
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(question.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {question.viewCount} vues</span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-slate-600 whitespace-pre-line">{question.body}</p>
        </div>

        {/* Answers */}
        <div className="mt-8">
          <h2 className="text-lg font-bold text-slate-900">{answerList.length} réponse{answerList.length > 1 ? 's' : ''}</h2>
          <div className="mt-4 space-y-4">
            {answerList.map((a) => (
              <div key={a.id} className="rounded-xl border border-border bg-white p-5 shadow-xs">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
                    {(a.guestName ?? 'A')[0]}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">{a.guestName ?? 'Anonyme'}</span>
                      {a.isProfessional && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold text-primary">
                          <BadgeCheck className="h-3 w-3" /> Artisan vérifié
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] text-slate-400">{new Date(a.createdAt).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 whitespace-pre-line">{a.body}</p>
                {a.upvotes > 0 && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-slate-400">
                    <ThumbsUp className="h-3 w-3" /> {a.upvotes} utile{a.upvotes > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Answer form */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-sm">
          <h3 className="text-base font-bold text-slate-900">Répondre à cette question</h3>
          {actionData?.success ? (
            <div className="mt-4 rounded-lg bg-emerald-50 p-4 text-center text-sm text-emerald-700">
              Merci pour votre réponse ! Elle a été publiée.
            </div>
          ) : (
            <Form method="post" className="mt-4 space-y-3">
              <textarea name="body" required minLength={10} rows={4} placeholder="Partagez votre expertise..."
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
              <div className="grid grid-cols-2 gap-3">
                <input name="name" required placeholder="Votre nom *"
                  className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
                <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-600 cursor-pointer has-[:checked]:border-primary has-[:checked]:bg-primary-50">
                  <input type="checkbox" name="isPro" value="true" className="rounded border-slate-300 text-primary" />
                  Je suis artisan
                </label>
              </div>
              {actionData?.error && <p className="text-sm text-red-500">{actionData.error}</p>}
              <button type="submit" disabled={navigation.state === 'submitting'}
                className="rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-50 btn-press">
                {navigation.state === 'submitting' ? 'Publication...' : 'Publier ma réponse'}
              </button>
            </Form>
          )}
        </div>

        {/* CTA */}
        <div className="mt-8 rounded-2xl bg-primary/5 p-6 text-center">
          <p className="text-sm font-semibold text-slate-900">Besoin d'un devis pour votre projet ?</p>
          <Link to="/demande-devis" className="mt-3 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover btn-press">
            Demander un devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  )
}
